// Pure helpers for the Tech Dashboard Gantt (pages/TechDashboard.tsx and
// components/devplan/*). Nothing here recomputes a figure the database
// already derives: variance, overdue, dependency state and blocks count all
// come from v_dev_plan_gantt exactly as the query returns them. What lives
// here is filtering, sorting, formatting for display, and the date-to-pixel
// placement the chart needs, none of which is a business figure.
//
// MISSING RENDERS AS MISSING, NEVER AS ZERO. An item with no planned_end is
// not a zero-length bar sitting on today, and a null variance is not "0 days
// late". Every formatter below returns an explicit words-based fallback
// rather than a number that looks real but isn't.

export type GanttItem = {
  item_id: string
  ref: string
  phase_key: string
  phase_label: string | null
  phase_sort_order: number | null
  title: string
  status: string
  planned_start: string | null
  planned_end: string | null
  actual_completion: string | null
  variance_days: number | null
  is_overdue: boolean
  owner_person_id: string | null
  owner_name: string | null
  blocked_reason: string | null
  dependency_state: 'clear' | 'waiting' | 'waiting_on_decision'
  blocks_count: number
  depends_on_ids: string[]
}

export type PhaseSummary = {
  phase_key: string
  label: string
  sort_order: number
  item_count: number
  not_started_count: number
  in_progress_count: number
  blocked_count: number
  awaiting_decision_count: number
  on_hold_count: number
  done_count: number
  superseded_count: number
  overdue_count: number
  last_planned_end: string | null
  effort_days_remaining: number | string | null
}

export type DevPlanRefsFeed = {
  phases: { phase_key: string; label: string; sort_order: number }[]
  statuses: string[]
  assignees: { email: string; display_name: string | null; person_id: string | null }[]
}

export const UNASSIGNED_OWNER = 'Unassigned'

/** Badge variant per status. Semantic colour, kept separate from brand crimson. */
export const STATUS_BADGE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'warning' | 'success' | 'outline'
> = {
  not_started: 'outline',
  in_progress: 'secondary',
  blocked: 'destructive',
  awaiting_decision: 'warning',
  on_hold: 'secondary',
  done: 'success',
  superseded: 'outline',
}

export function statusLabel(status: string): string {
  return status.replace(/_/g, ' ')
}

/** A date column that may be null. Never rendered as today, never as blank. */
export function formatDate(d: string | null): string {
  return d ?? 'no date set'
}

export function formatVariance(v: number | null): string {
  if (v === null || v === undefined) return 'no variance yet'
  if (v === 0) return 'on time'
  return v > 0 ? `${v} day${v === 1 ? '' : 's'} late` : `${-v} day${-v === 1 ? '' : 's'} early`
}

export function formatOwner(name: string | null): string {
  return name ?? UNASSIGNED_OWNER
}

export type DevPlanFilterState = {
  phaseKey: string | null // null = all phases
  status: string | null // null = all statuses
  owner: string | null // null = all owners; UNASSIGNED_OWNER is its own value
}

export const EMPTY_DEV_PLAN_FILTER: DevPlanFilterState = {
  phaseKey: null,
  status: null,
  owner: null,
}

export function filterDevPlanItems(items: GanttItem[], f: DevPlanFilterState): GanttItem[] {
  return items.filter((i) => {
    if (f.phaseKey && i.phase_key !== f.phaseKey) return false
    if (f.status && i.status !== f.status) return false
    if (f.owner && formatOwner(i.owner_name) !== f.owner) return false
    return true
  })
}

/** Distinct owner names actually present in the current item set, sorted, Unassigned last. */
export function ownerOptionsFromItems(items: GanttItem[]): string[] {
  const names = new Set<string>()
  let sawUnassigned = false
  for (const i of items) {
    if (i.owner_name) names.add(i.owner_name)
    else sawUnassigned = true
  }
  const sorted = [...names].sort((a, b) => a.localeCompare(b))
  return sawUnassigned ? [...sorted, UNASSIGNED_OWNER] : sorted
}

// ---------------------------------------------------------------------------
// Chart geometry. A pure date <-> pixel mapping, no rendering.

export type ChartDomain = {
  start: Date
  end: Date
  totalDays: number
}

const DAY_MS = 24 * 60 * 60 * 1000

function parseDate(d: string | null): Date | null {
  if (!d) return null
  const parsed = new Date(d + 'T00:00:00Z')
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS)
}

/**
 * The date range the chart draws across, padded a week on each side. Falls
 * back to a today-centred window when the visible items carry no dates at
 * all, so the chart still renders something rather than a domain of zero
 * width.
 */
export function computeChartDomain(items: GanttItem[]): ChartDomain {
  let min: Date | null = null
  let max: Date | null = null
  for (const i of items) {
    for (const raw of [i.planned_start, i.planned_end, i.actual_completion]) {
      const d = parseDate(raw)
      if (!d) continue
      if (!min || d < min) min = d
      if (!max || d > max) max = d
    }
  }
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z')
  if (!min || !max) {
    min = new Date(today.getTime() - 30 * DAY_MS)
    max = new Date(today.getTime() + 90 * DAY_MS)
  }
  const start = new Date(min.getTime() - 7 * DAY_MS)
  const end = new Date(max.getTime() + 7 * DAY_MS)
  return { start, end, totalDays: Math.max(1, daysBetween(start, end)) }
}

export const PX_PER_DAY = 8

export function dateToX(d: string | null, domain: ChartDomain): number | null {
  const parsed = parseDate(d)
  if (!parsed) return null
  return daysBetween(domain.start, parsed) * PX_PER_DAY
}

export function todayX(domain: ChartDomain): number {
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z')
  return daysBetween(domain.start, today) * PX_PER_DAY
}

export function domainWidthPx(domain: ChartDomain): number {
  return domain.totalDays * PX_PER_DAY
}

/** Month tick marks for the time axis: {x, label} for the first of each month in range. */
export function monthTicks(domain: ChartDomain): { x: number; label: string }[] {
  const ticks: { x: number; label: string }[] = []
  const cursor = new Date(Date.UTC(domain.start.getUTCFullYear(), domain.start.getUTCMonth(), 1))
  while (cursor <= domain.end) {
    if (cursor >= domain.start) {
      const x = daysBetween(domain.start, cursor) * PX_PER_DAY
      const label = cursor.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })
      ticks.push({ x, label })
    }
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }
  return ticks
}

export type SortColumn =
  | 'ref'
  | 'phase_label'
  | 'title'
  | 'status'
  | 'planned_end'
  | 'variance_days'
  | 'owner_name'

export function sortDevPlanItems(
  items: GanttItem[],
  column: SortColumn,
  direction: 'asc' | 'desc',
): GanttItem[] {
  const dir = direction === 'desc' ? -1 : 1
  // Nulls sort last regardless of direction: a missing value is not "small",
  // so it must not become "first" just because the direction flipped.
  return [...items].sort((a, b) => {
    const av = a[column]
    const bv = b[column]
    if (av === null && bv === null) return 0
    if (av === null) return 1
    if (bv === null) return -1
    if (av < bv) return -1 * dir
    if (av > bv) return 1 * dir
    return 0
  })
}
